import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo } from "react";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { View } from "@com.mgmtp.a12.client/client-core/lib/core/view";
import { DefaultWidgetMap, WidgetMap } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/core/view";
import { TreeEngineFactories } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/client";
import { Select } from "@com.mgmtp.a12.widgets/widgets-core/lib/input/select";
import { ModalNotification } from "@com.mgmtp.a12.widgets/widgets-core/lib/modal-notification/main/modal-notification.view";
import { ButtonGroup } from "@com.mgmtp.a12.widgets/widgets-core/lib/button-group/main/button-group.view";
import { Button } from "@com.mgmtp.a12.widgets/widgets-core/lib/button/main/button.view";
import { LayoutGrid } from "@com.mgmtp.a12.widgets/widgets-core";

import { getViewHtmlId } from "../../focusHandling/sagas";

import { SET_CONTACT_DATA } from "../actions";
import { DEFAULT_EMPFAENGER_TYP, EMPFAENGER_TYPS } from "../const";
import { EmpfaengerTyp, isContactSelectionData } from "../types";
import { ContactSelectionModalDescriptor } from "../descriptors";

import Grid = LayoutGrid.Grid;
import Row = LayoutGrid.Row;
import Column = LayoutGrid.Column;

export const CustomContactTreeEngine: View.ViewComponent = function CustomContactTreeEngine(props) {
	const activityId = props.activityId;
	const dispatch = useDispatch();
	const activityData = useSelector(ActivitySelectors.data(activityId));
	const activity = useSelector(ActivitySelectors.activityById(activityId));

	const entryMode = ContactSelectionModalDescriptor.isInstance(activity?.descriptor)
		? activity.descriptor.entryMode
		: null;
	const showEmpfaengerTyp = entryMode !== "email";

	const empfaengerTyp: EmpfaengerTyp = isContactSelectionData(activityData)
		? (activityData.empfaengerTyp as EmpfaengerTyp)
		: DEFAULT_EMPFAENGER_TYP;

	const handleEmpfaengerTypChange = useCallback(
		(value: string) => {
			dispatch(ActivityActions.setData({ activityId, data: { ...activityData, empfaengerTyp: value } }));
		},
		[activityId, activityData, dispatch]
	);

	const onAbbrechenClick = useCallback(() => {
		dispatch(ActivityActions.cancel({ activityId }));
	}, [dispatch, activityId]);

	const onConfirmClick = useCallback(() => {
		dispatch(SET_CONTACT_DATA({ activityId }));
	}, [dispatch, activityId]);

	const widgetMap: WidgetMap = useMemo(
		() => ({
			...DefaultWidgetMap,
			Button: props => {
				return <DefaultWidgetMap.Button {...props} disabled={false} />;
			}
		}),
		[activityId]
	);

	return (
		<ModalNotification
			id={getViewHtmlId(activityId)}
			title={"Kontakte auswählen"}
			enableCloseButton={true}
			icon={" "}
			variant="info"
			closeOnOutsideClick={true}
			onClose={onAbbrechenClick}
			closeOnEsc={true}
			footer={
				<ButtonGroup alignment="right">
					<Button label={"Abbrechen"} secondary onClick={onAbbrechenClick} />
					<Button label={"Kontakte auswählen"} primary onClick={onConfirmClick} />
				</ButtonGroup>
			}
		>
			<Grid>
				<Row>
					<Column size={{ sm: 12, md: 12, lg: 12 }}>
						<p>
							Wählen Sie Kontakte aus den folgenden Kontaktlisten aus. Bereits hinzugefügte Kontakte und
							leere Kontaktlisten werden ausgeblendet.
						</p>
					</Column>
				</Row>
				{showEmpfaengerTyp && (
					<Row>
						<Column size={{ lg: 2, md: 3, sm: 4 }}>
							<Select
								label={"Typ"}
								value={empfaengerTyp}
								onValueChanged={handleEmpfaengerTypChange}
								items={EMPFAENGER_TYPS}
								id={getViewHtmlId(activityId) + "-empfaenger-typ-select"}
							/>
						</Column>
					</Row>
				)}
			</Grid>

			<TreeEngineFactories.ViewComponent {...props} widgetMap={widgetMap} />
		</ModalNotification>
	);
};
